import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Form, Spinner, Table, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Layout from "../../../universal/layout/Layout";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import RecouvrementDetailModal from "./RecouvrementDetailModal";
import HelpTip from "app/orderTool/components/HelpTip";
import DocumentsButton from "app/orderTool/components/DocumentsButton";
import AlphaBanner from "app/orderTool/components/AlphaBanner";
import { moneyCellStyle, stickyTable, EmptyState } from "app/orderTool/components/pageUi";
import {
  getImpayes,
  getRecouvrementConstants,
  getRelanceMultipleBrouillon,
  postRelanceMultiple,
  postRecouvrementActionsGroupees,
} from "state/recouvrement/actions";
import { filterImpayesByCdc } from "./recouvrementFilters";
import { STATUT_LABELS, labelFor, euros, retardBadgeVariant } from "./recouvrementLabels";

const CHARGES = ["Julie", "Valentin", "Camille"];

// Valeur des selects « action groupée » quand le champ ne doit pas être touché.
const KEEP = "";
// Valeur pour vider explicitement un champ (chargé de clientèle).
const CLEAR = "__clear__";

const RecouvrementImpayes = () => {
  // Titre de l'onglet navigateur (sinon il reste celui de la page précédente).
  useEffect(() => {
    const prev = document.title;
    document.title = "Impayés — Le Moulin";
    return () => { document.title = prev; };
  }, []);

  const dispatch = useDispatch();
  const impayes = useSelector((state) => state.recouvrement.impayes);
  const count = useSelector((state) => state.recouvrement.count);
  const masqueesRelance = useSelector((state) => state.recouvrement.masqueesRelance);
  const isLoading = useSelector((state) => state.recouvrement.isLoading);
  const isInitialized = useSelector((state) => state.recouvrement.isInitialized);
  const constants = useSelector((state) => state.recouvrement.constants);

  const [chargeFiltre, setChargeFiltre] = useState("");
  const [statutFiltre, setStatutFiltre] = useState("");
  const [trancheFiltre, setTrancheFiltre] = useState("");
  const [relanceJours, setRelanceJours] = useState(""); // seuil J+N avant relance interne BO
  const [recherche, setRecherche] = useState(""); // recherche entreprise / client / facture
  // Relance multi-factures
  const [selectedIds, setSelectedIds] = useState([]);
  const [relanceOpen, setRelanceOpen] = useState(false);
  const [relanceLoading, setRelanceLoading] = useState(false);
  const [relanceSending, setRelanceSending] = useState(false);
  const [relanceEmail, setRelanceEmail] = useState("");
  const [relanceObjet, setRelanceObjet] = useState("");
  const [relanceCorps, setRelanceCorps] = useState("");
  // Action groupée de suivi (statut / chargé / prochaine action / note)
  const [actionOpen, setActionOpen] = useState(false);
  const [actionSending, setActionSending] = useState(false);
  const [actionStatut, setActionStatut] = useState(KEEP);
  const [actionCharge, setActionCharge] = useState(KEEP);
  const [actionProchaine, setActionProchaine] = useState("");
  const [actionClearProchaine, setActionClearProchaine] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalGroupedId, setModalGroupedId] = useState(null);
  const [modalCard, setModalCard] = useState(null);

  const apiFilters = useMemo(() => {
    const f = { masquer_soldes: "1" };
    if (statutFiltre) f.statut = statutFiltre;
    const tranches = constants?.tranches_retard || [];
    const tranche = tranches.find((t) => t.id === trancheFiltre);
    if (tranche) {
      if (tranche.min != null) f.retard_min = tranche.min;
      if (tranche.max != null) f.retard_max = tranche.max;
    }
    if (relanceJours !== "" && Number(relanceJours) > 0) f.relance_apres_jours = relanceJours;
    return f;
  }, [statutFiltre, trancheFiltre, relanceJours, constants]);

  const reload = useCallback(() => {
    dispatch(getImpayes(apiFilters));
  }, [dispatch, apiFilters]);

  useEffect(() => {
    reload();
    if (!constants) {
      dispatch(getRecouvrementConstants());
    }
  }, [reload, dispatch, constants]);

  // Pré-remplit le seuil de relance avec la valeur configurée côté serveur.
  useEffect(() => {
    if (relanceJours === "" && constants?.relance_apres_jours != null) {
      setRelanceJours(String(constants.relance_apres_jours));
    }
  }, [constants, relanceJours]);

  const filteredImpayes = useMemo(() => {
    let list = filterImpayesByCdc(impayes, chargeFiltre);
    const q = recherche.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        `${r.client_nom || ""} ${r.entreprise_nom || ""} ${r.invoice_number || ""}`.toLowerCase().includes(q)
      );
    }
    return list;
  }, [impayes, chargeFiltre, recherche]);

  const openDetail = (row) => {
    setModalGroupedId(row.grouped_order_id);
    setModalCard(row);
    setModalOpen(true);
  };

  const toggleSelect = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Sélection de masse : porte uniquement sur les lignes actuellement visibles
  // (filtres + recherche appliqués), jamais sur toute la base des impayés.
  const visibleIds = useMemo(
    () => filteredImpayes.map((r) => r.grouped_order_id),
    [filteredImpayes]
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () =>
    setSelectedIds((s) =>
      allVisibleSelected
        ? s.filter((id) => !visibleIds.includes(id))
        : [...new Set([...s, ...visibleIds])]
    );

  const resetActionForm = () => {
    setActionStatut(KEEP);
    setActionCharge(KEEP);
    setActionProchaine("");
    setActionClearProchaine(false);
    setActionNote("");
  };

  const openAction = () => {
    if (!selectedIds.length) return;
    resetActionForm();
    setActionOpen(true);
  };

  const actionPayload = () => {
    const payload = {};
    if (actionStatut !== KEEP) payload.statut = actionStatut;
    if (actionCharge !== KEEP) payload.charge_de_clientele = actionCharge === CLEAR ? null : actionCharge;
    if (actionClearProchaine) payload.prochaine_action = null;
    else if (actionProchaine) payload.prochaine_action = actionProchaine;
    if (actionNote.trim()) payload.note = actionNote.trim();
    return payload;
  };

  const confirmAction = async () => {
    const payload = actionPayload();
    if (Object.keys(payload).length === 0) {
      toast.error("Choisissez au moins une modification à appliquer");
      return;
    }
    setActionSending(true);
    const resp = await dispatch(postRecouvrementActionsGroupees(selectedIds, payload));
    setActionSending(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Impossible d'appliquer l'action groupée");
      return;
    }
    const nb = resp?.nb ?? selectedIds.length;
    const ignores = resp?.ignores ?? 0;
    toast.success(
      `${nb} facture(s) mise(s) à jour${ignores > 0 ? ` — ${ignores} ignorée(s) (non éligible)` : ""}`
    );
    setActionOpen(false);
    setSelectedIds([]);
    reload();
  };

  const openRelance = async () => {
    if (!selectedIds.length) return;
    setRelanceOpen(true);
    setRelanceLoading(true);
    const resp = await dispatch(getRelanceMultipleBrouillon(selectedIds));
    setRelanceLoading(false);
    const d = resp || {};
    setRelanceEmail(d.email || "");
    setRelanceObjet(d.objet || "");
    setRelanceCorps(d.corps || "");
  };

  const confirmRelance = async () => {
    setRelanceSending(true);
    const resp = await dispatch(postRelanceMultiple(selectedIds, relanceEmail));
    setRelanceSending(false);
    if (resp?.error) {
      toast.error(resp?.message || "Impossible d'enregistrer la relance");
      return;
    }
    toast.success(`Relance enregistrée (${resp?.nb ?? selectedIds.length} facture(s))`);
    setRelanceOpen(false);
    setSelectedIds([]);
    reload();
  };

  const boardLoading = isLoading && !isInitialized;

  return (
    <Layout>
      <div style={{ padding: "15px" }}>
        <AlphaBanner feature="Recouvrement — factures impayées" />
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <h4 className="mb-0 mr-3">
            Recouvrement — factures impayées
            <HelpTip title="Les factures impayées">
              <p className="mb-1">Cette liste regroupe les <strong>factures B2B (F/B/…) échues et non payées</strong> : envoyées au client, dont la date limite de paiement est dépassée et sans encaissement enregistré.</p>
              <p className="mb-1">L'échéance correspond à la date de la facture <strong>+ 32 jours</strong> (délai de paiement standard).</p>
              <p className="mb-0">Cliquez une ligne pour <strong>suivre le recouvrement</strong> : statut, prochaine action, notes et brouillon de relance. Le lien <strong>« Facture PDF »</strong> ouvre le document.</p>
            </HelpTip>
            {isLoading && isInitialized && (
              <Spinner animation="border" size="sm" variant="success" className="ml-2" />
            )}
            <Badge variant="secondary" className="ml-2">{filteredImpayes.length} / {count}</Badge>
            {masqueesRelance > 0 && (
              <Badge variant="light" className="ml-2 border" title="Factures impayées mais encore couvertes par les relances automatiques VosFactures (avant le seuil J+N)">
                +{masqueesRelance} gérées par VosFactures (&lt; seuil)
              </Badge>
            )}
          </h4>
          <div className="d-flex align-items-center flex-wrap">
            <Form.Control
              size="sm"
              style={{ width: "220px" }}
              className="mr-2 mb-1"
              placeholder="Rechercher entreprise / client / facture…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <Form.Control
              as="select"
              size="sm"
              style={{ width: "200px" }}
              className="mr-2 mb-1"
              value={chargeFiltre}
              onChange={(e) => setChargeFiltre(e.target.value)}
            >
              <option value="">Tous les chargés</option>
              {CHARGES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Form.Control>
            <Form.Control
              as="select"
              size="sm"
              style={{ width: "180px" }}
              className="mr-2 mb-1"
              value={statutFiltre}
              onChange={(e) => setStatutFiltre(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              {(constants?.statuts || Object.keys(STATUT_LABELS)).map((s) => (
                <option key={s} value={s}>{labelFor(constants?.statuts_labels || STATUT_LABELS, s)}</option>
              ))}
            </Form.Control>
            <Form.Control
              as="select"
              size="sm"
              style={{ width: "150px" }}
              className="mr-2 mb-1"
              value={trancheFiltre}
              onChange={(e) => setTrancheFiltre(e.target.value)}
            >
              <option value="">Tous retards</option>
              {(constants?.tranches_retard || []).map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </Form.Control>
            <div className="d-flex align-items-center mr-2 mb-1" style={{ whiteSpace: "nowrap" }}>
              <span className="mr-1" style={{ fontSize: "0.85rem" }}>Relance après (j)</span>
              <Form.Control
                type="number"
                min="1"
                size="sm"
                style={{ width: "70px" }}
                value={relanceJours}
                onChange={(e) => setRelanceJours(e.target.value)}
                title="Nombre de jours après l'émission de la facture avant qu'elle entre dans le recouvrement BO"
              />
              <HelpTip title="Délai avant relance interne" placement="bottom">
                <p className="mb-1">Une facture n'apparaît ici qu'<strong>après ce délai</strong> (en jours depuis son émission).</p>
                <p className="mb-0">Avant, VosFactures gère ses relances automatiques (J+3, J+10, J+20). Valeur par défaut configurée côté serveur ; modifiable ici pour la vue en cours.</p>
              </HelpTip>
            </div>
            <Button size="sm" variant="outline-success" className="mb-1" onClick={reload}>
              Actualiser
            </Button>
            {selectedIds.length > 0 && (
              <>
                <Button size="sm" variant="warning" className="mb-1 ml-1" onClick={openRelance}>
                  ✉️ Préparer relance ({selectedIds.length})
                </Button>
                <Button size="sm" variant="outline-primary" className="mb-1 ml-1" onClick={openAction}>
                  ⚙️ Action groupée ({selectedIds.length})
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  className="mb-1 ml-1"
                  onClick={() => setSelectedIds([])}
                >
                  Tout désélectionner
                </Button>
              </>
            )}
          </div>
        </div>

        <LoadingOverlay show={boardLoading} message="Chargement des impayés…" />

        {!boardLoading && filteredImpayes.length === 0 && (
          <EmptyState
            icon="🎉"
            title="Aucune facture impayée"
            hint="Aucune facture F/B échue et non réglée avec ces filtres."
          />
        )}

        {filteredImpayes.length > 0 && (
          <div className="table-responsive">
            <Table hover striped size="sm" className={`bg-white ${stickyTable}`}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <Form.Check
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      title="Sélectionner / désélectionner toutes les factures affichées"
                    />
                  </th>
                  <th>Facture</th>
                  <th>Client / Entreprise</th>
                  <th className="text-right">Montant TTC</th>
                  <th>Échéance</th>
                  <th>
                    Retard
                    <HelpTip title="Retard" placement="bottom">
                      Nombre de jours écoulés depuis l'échéance (facture + 32 j). La couleur s'assombrit avec l'ancienneté : plus c'est rouge/foncé, plus la facture traîne.
                    </HelpTip>
                  </th>
                  <th>
                    Statut
                    <HelpTip title="Statut de recouvrement" placement="bottom">
                      Où en est la relance de cette facture : <strong>À relancer</strong>, relance <strong>en cours</strong>, <strong>promesse</strong> de paiement, <strong>litige</strong>… Se met à jour depuis la fiche détail.
                    </HelpTip>
                  </th>
                  <th>Prochaine action</th>
                  <th>Dernière note</th>
                  <th>CDC</th>
                  <th>Document</th>
                </tr>
              </thead>
              <tbody>
                {filteredImpayes.map((row) => (
                  <tr
                    key={row.grouped_order_id}
                    style={{ cursor: "pointer" }}
                    onClick={() => openDetail(row)}
                  >
                    <td onClick={(e) => e.stopPropagation()} style={{ width: 28 }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(row.grouped_order_id)}
                        onChange={() => toggleSelect(row.grouped_order_id)}
                      />
                    </td>
                    <td>
                      <b>{row.invoice_number}</b>
                      <br />
                      <small className="text-muted">{row.order_number}</small>
                      {/* Presta annulée sans avoir : la facture reste due tant
                          que l'avoir n'a pas été émis — à régulariser. */}
                      {row.annulee_sans_avoir && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Badge variant="danger">⚠ Annulée sans avoir</Badge>
                          <HelpTip title="Annulée sans avoir" placement="right">
                            <p className="mb-1">
                              La prestation a été <strong>annulée</strong>
                              {row.annulee_le
                                ? ` le ${new Date(row.annulee_le).toLocaleDateString("fr-FR")}`
                                : ""}, mais <strong>aucune facture d'avoir</strong> n'a été émise.
                            </p>
                            <p className="mb-1">
                              Une facture envoyée ne s'annule pas toute seule : tant que l'avoir
                              n'existe pas, la somme reste <strong>comptablement due</strong> par le
                              client. La facture reste donc dans les impayés.
                            </p>
                            <p className="mb-0">
                              À faire : créer l'avoir dans VosFactures. Dès qu'il existe (payé ou
                              non), la facture sort automatiquement de cette liste.
                            </p>
                          </HelpTip>
                        </div>
                      )}
                    </td>
                    <td>
                      {row.client_nom || "—"}
                      {row.entreprise_nom ? (
                        <>
                          <br />
                          <small>{row.entreprise_nom}</small>
                        </>
                      ) : null}
                    </td>
                    <td style={moneyCellStyle}>{euros(row.montant_du_centimes)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{row.date_echeance ? new Date(row.date_echeance).toLocaleDateString("fr-FR") : "—"}</td>
                    <td>
                      <Badge variant={retardBadgeVariant(row.jours_retard)}>
                        {row.jours_retard} j
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="info">
                        {labelFor(constants?.statuts_labels || STATUT_LABELS, row.suivi?.statut)}
                      </Badge>
                      {row.suivi?.derniere_relance_le && (
                        <div>
                          <small className="text-muted" title={row.suivi.derniere_relance_email || ""}>
                            Relancé {new Date(row.suivi.derniere_relance_le).toLocaleDateString("fr-FR")}
                            {row.suivi.derniere_relance_email ? ` → ${row.suivi.derniere_relance_email}` : ""}
                          </small>
                        </div>
                      )}
                    </td>
                    <td>
                      {row.suivi?.prochaine_action
                        ? new Date(row.suivi.prochaine_action).toLocaleDateString("fr-FR")
                        : "—"}
                    </td>
                    <td>
                      <small>{row.suivi?.derniere_note || "—"}</small>
                    </td>
                    <td>{row.charge_de_clientele || "—"}</td>
                    <td>
                      {row.documents && row.documents.length > 0 ? (
                        <DocumentsButton documents={row.documents} stopPropagation />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <RecouvrementDetailModal
          show={modalOpen}
          groupedOrderId={modalGroupedId}
          impayeCard={modalCard}
          constants={constants}
          onClose={() => setModalOpen(false)}
          onSaved={reload}
        />

        <Modal show={actionOpen} onHide={() => setActionOpen(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Action groupée — {selectedIds.length} facture(s)</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              Les champs laissés sur « Ne pas modifier » restent inchangés sur chaque facture.
              Chaque modification est journalisée dans les notes de suivi.
            </p>
            <Form.Group>
              <Form.Label>Statut</Form.Label>
              <Form.Control
                as="select"
                value={actionStatut}
                onChange={(e) => setActionStatut(e.target.value)}
              >
                <option value={KEEP}>— Ne pas modifier —</option>
                {(constants?.statuts || Object.keys(STATUT_LABELS)).map((s) => (
                  <option key={s} value={s}>{labelFor(constants?.statuts_labels || STATUT_LABELS, s)}</option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Chargé de clientèle</Form.Label>
              <Form.Control
                as="select"
                value={actionCharge}
                onChange={(e) => setActionCharge(e.target.value)}
              >
                <option value={KEEP}>— Ne pas modifier —</option>
                <option value={CLEAR}>Aucun (vider)</option>
                {CHARGES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Prochaine action</Form.Label>
              <Form.Control
                type="date"
                value={actionProchaine}
                disabled={actionClearProchaine}
                onChange={(e) => setActionProchaine(e.target.value)}
              />
              <Form.Check
                type="checkbox"
                className="mt-1"
                id="action-clear-prochaine"
                label="Effacer la prochaine action"
                checked={actionClearProchaine}
                onChange={(e) => setActionClearProchaine(e.target.checked)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Note (optionnelle)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder="Ajoutée à l'historique de chaque facture sélectionnée"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setActionOpen(false)} disabled={actionSending}>Annuler</Button>
            <Button variant="success" onClick={confirmAction} disabled={actionSending}>
              {actionSending ? <><Spinner animation="border" size="sm" className="mr-1" /> Application…</> : "Appliquer"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={relanceOpen} onHide={() => setRelanceOpen(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Préparer la relance — {selectedIds.length} facture(s)</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ position: "relative", minHeight: 140 }}>
            <LoadingOverlay show={relanceLoading} message="Préparation du brouillon…" />
            <Form.Group>
              <Form.Label>Email destinataire</Form.Label>
              <Form.Control type="email" value={relanceEmail} onChange={(e) => setRelanceEmail(e.target.value)} placeholder="email de facturation" />
              <Form.Text className="text-muted">Pré-rempli avec l'email de facturation — modifiable.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Objet</Form.Label>
              <Form.Control value={relanceObjet} onChange={(e) => setRelanceObjet(e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Message</Form.Label>
              <Form.Control as="textarea" rows={10} value={relanceCorps} onChange={(e) => setRelanceCorps(e.target.value)} />
            </Form.Group>
            <a
              href={`mailto:${relanceEmail}?subject=${encodeURIComponent(relanceObjet)}&body=${encodeURIComponent(relanceCorps)}`}
              className="btn btn-outline-primary btn-sm"
            >
              ↗ Ouvrir dans ma messagerie
            </a>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setRelanceOpen(false)} disabled={relanceSending}>Annuler</Button>
            <Button variant="success" onClick={confirmRelance} disabled={relanceSending || relanceLoading}>
              {relanceSending ? <><Spinner animation="border" size="sm" className="mr-1" /> Enregistrement…</> : "J'ai envoyé la relance"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default RecouvrementImpayes;
